import { Link } from 'react-router-dom'

const guide = {
  slug: 'dns-porn-blocker',
  category: 'holds',
  kicker: 'The mechanism · DNS',
  title: 'DNS porn blockers explained: how they work, and the three ways around them',
  seoTitle: 'DNS Porn Blocker Explained: How It Works, Where It Fails',
  description: `A DNS porn blocker refuses whole categories before any browser gets a say. How it works, how to set one on each device, and the three doors it leaves open.`,
  published: '2026-09-03',
  minutes: 8,
  intro: `"Just use a filtering DNS" is the most common advice on every forum
          about blocking porn, and it's good advice: it's the right layer. It
          is also incomplete, because the people giving it rarely say how the
          filter gets switched off. This guide explains what a DNS blocker
          does, how to set one up on iPhone, Mac, Windows and a router, and
          exactly where it stops holding.`,
  next: ['porn-blocker-that-blocks-vpn', 'does-incognito-bypass-porn-blockers', 'porn-blocker-you-cant-uninstall'],
  body: (
    <>
      <h2>What DNS is, in one paragraph</h2>
      <p>
        Every time any app on your device wants to reach a website, it first
        asks a <b>resolver</b>: "where is this domain?" The resolver answers
        with an address, and only then does the connection happen. This
        question is asked by Safari, Chrome, a private tab, the browser inside
        Instagram, a video app, everything. The resolver is set once, for the
        whole device or the whole network.
      </p>

      <h2>What a filtering resolver does</h2>
      <p>
        A filtering resolver keeps a classification of domains by
        <b> category</b>: pornography, nudity, adult themes, gambling, dating,
        and so on. When a device asks for a domain in a blocked category, the
        resolver answers "nowhere", or points to a block page, and the site
        never loads. Three things follow from this design:
      </p>
      <ul>
        <li><b>It's device-wide.</b> The browser is not a variable. Incognito, another browser, an in-app browser, an app that isn't a browser: same question, same refusal.</li>
        <li><b>It's a category, not a list.</b> Nobody maintains a list of every adult site by hand; new domains appear daily. Classification catches them by what they are, not by name.</li>
        <li><b>It keeps no history of what you did.</b> The resolver sees domain questions, not pages, not searches, not what was on the screen. Filtering by category needs no record of you at all. (What a given operator logs is their policy; ours is in the <Link to="/confidentialite">privacy policy</Link>.)</li>
      </ul>
      <p>
        Most public filtering resolvers publish "family" addresses that block
        adult content, and some let you pick categories from an account. The
        addresses are on their sites.
      </p>

      <h2>Setting a filtering DNS on each device</h2>
      <h3>iPhone and iPad</h3>
      <p>
        Per Wi-Fi network: <b>Settings → Wi-Fi → (i) → Configure DNS →
        Manual</b>. That covers one network only. For all networks including
        mobile data, you install a <b>configuration profile</b> that sets an
        encrypted resolver, or an app that provides one. Both cover every app
        on the phone.
      </p>
      <h3>Mac</h3>
      <p><b>System Settings → Network → your connection → Details → DNS</b>, add the resolver's addresses, remove the others.</p>
      <h3>Windows</h3>
      <p><b>Settings → Network &amp; internet → your adapter → DNS server assignment → Edit → Manual</b>. Windows 11 also lets you turn on encryption (DNS over HTTPS) there.</p>
      <h3>Router</h3>
      <p>
        Set the resolver in the router's LAN/DHCP settings and every device on
        the Wi-Fi inherits it. Steps and limits in
        <Link to="/guides/how-to-block-porn-on-wifi-router"> the router guide</Link>.
      </p>

      <h2>Door one: change it back</h2>
      <p>
        The setting you just made is a setting. The same screen that took
        the filtering resolver takes any other one, or "Automatic". On a
        computer you're the administrator; on an iPhone, a profile you
        installed is removed in <b>Settings → General → VPN &amp; Device
        Management → Remove Profile</b>, four taps, no password. A DNS filter
        set by you is a note on the door in your own handwriting.
      </p>

      <h2>Door two: a VPN</h2>
      <p>
        A VPN app tunnels the device's traffic to the VPN provider, and most
        of them replace the resolver with their own while connected. The
        filtering resolver is no longer being asked. Nothing is broken; it's
        simply bypassed. Install a VPN app, tap connect. What closes this
        door, device by device:
        <Link to="/guides/porn-blocker-that-blocks-vpn"> a porn blocker that blocks VPNs</Link>.
      </p>

      <h2>Door three: the browser's own encrypted DNS</h2>
      <p>
        Modern browsers can resolve domains themselves over HTTPS, ignoring
        the system resolver. Chrome's <b>Secure DNS</b> and Firefox's
        <b> DNS over HTTPS</b> both have a "custom provider" option where you
        type any resolver you like. On a computer, that's a text field in
        the browser's settings. On iPhone and iPad, browsers use the system's
        DNS path, so this door doesn't exist there.
      </p>

      <h2>What a DNS filter needs to actually hold</h2>
      <p>
        The filter is right. The <em>attachment</em> is the product. A DNS
        blocker holds when three things are true at once:
      </p>
      <ol>
        <li>The resolver setting can't be changed by the person using the device.</li>
        <li>A VPN can't be added.</li>
        <li>Loosening anything is slow on purpose: a delay long enough for an urge to pass.</li>
      </ol>
      <p>
        On iPhone and iPad, Apple's <b>supervision</b> makes the first two
        possible: a supervised device can carry a profile that cannot be
        removed and that forbids VPN configurations. That's the profile
        SELFCNTRL installs; the third point is the account rule that any
        loosening waits twenty-four hours while tightening is immediate. The
        setup is ten minutes, once:
        <Link to="/guides/how-to-block-porn-on-iphone"> the iPhone guide</Link>.
        On Mac and Windows, where no equivalent of supervision exists for a
        personal machine, SELFCNTRL installs the filter as an app tied to your
        account with curfews and the same delay; the phone is where the
        immovable lock goes, because the phone is where 2am happens.
      </p>

      <h2>DNS filtering, side by side</h2>
      <table>
        <thead>
          <tr><th></th><th>DNS set by you</th><th>DNS profile (iPhone)</th><th>DNS in a supervised profile</th></tr>
        </thead>
        <tbody>
          <tr><td>Every app, every browser, incognito</td><td>Yes</td><td>Yes</td><td>Yes</td></tr>
          <tr><td>Follows the device on any network</td><td>Per network</td><td>Yes</td><td>Yes</td></tr>
          <tr><td>Can be switched back</td><td>Yes, one screen</td><td>Yes, Remove Profile</td><td>No</td></tr>
          <tr><td>VPN walks around it</td><td>Yes</td><td>Yes</td><td>VPN can't be added</td></tr>
          <tr><td>Browser custom DoH walks around it</td><td>On a computer</td><td>No (iOS)</td><td>No (iOS)</td></tr>
        </tbody>
      </table>
    </>
  ),
  faq: [
    {
      q: 'Is a DNS porn blocker better than a browser extension?',
      a: `Yes, by construction. An extension sees one browser; a DNS filter
          answers the question every app asks before loading anything, so it
          covers all browsers, private windows, in-app browsers and apps.
          The remaining question is whether you can switch it off.`
    },
    {
      q: 'Does a DNS blocker slow down the internet?',
      a: `Not noticeably. A DNS lookup happens once per domain and takes a
          few milliseconds; the answer is cached. Nothing about your traffic
          is routed elsewhere, unlike a VPN.`
    },
    {
      q: 'Can a DNS blocker block part of a site, like NSFW on Reddit?',
      a: `No. DNS works on domains, not on pages or content inside a page.
          Either reddit.com resolves or it doesn't. Blocking a whole domain
          or a whole category is possible; blocking one kind of post within
          an app is not what DNS does.`
    },
    {
      q: 'Does a DNS blocker see my browsing?',
      a: `A resolver sees which domains a device asks about, not the pages,
          searches or content. Whether it keeps those requests depends on
          the operator. Category filtering itself needs no record of you.
          SELFCNTRL stores no browsing history and sends no reports.`
    }
  ]
}

export default guide
